import type { GridBox } from "@reco/shared/src/types/gridbox";

export type GridboxRequestParams = {
  recoDetailsHost?: string;
  countryId: string;
  languageId: string;
  recommendationItems: string[];
};

/**
 * Builds the Gridbox API URL based on the provided parameters
 * @param gridboxRequestParams The params needed for the Gridbox API call, including the reco products to get the Gridbox data for, the countryId, and the language
 * @returns The constructed Gridbox API URL as a string
 */
export const buildGridboxUrl = (gridboxRequestParams: GridboxRequestParams) => {
  const GRIDBOX_PATH = "p/api/gridboxes";
  const path = [
    gridboxRequestParams.recoDetailsHost,
    GRIDBOX_PATH,
    gridboxRequestParams.countryId,
    gridboxRequestParams.languageId,
  ].join("/");
  const queryStr = `erpNumbers=${gridboxRequestParams.recommendationItems.join(",")}`;
  return `${path}?${queryStr}`;
};

/**
 * Fetches Gridbox data from the Gridbox API
 * @param gridboxRequestParams The params needed for the Gridbox API call, including the reco products to get the Gridbox data for, the countryId, and the language
 * @returns A promise that resolves to an array of GridBox objects, sorted in the order of the requested recommendationItems
 */
export const getGridboxData = async (gridboxRequestParams: GridboxRequestParams): Promise<GridBox[]> => {
  try {
    const res = await fetch(buildGridboxUrl(gridboxRequestParams)).then((response) => {
      return response.json() as Promise<GridBox[]>;
    });

    // NEOSRF-359: Gridbox API returns gridboxes sorted by their erpNumber, not by the order of IDs we pass them
    // This sorting ensures we use the order of recommendationItems as requested
    res.sort((a, b) => {
      return (
        gridboxRequestParams.recommendationItems.indexOf(a.erpNumber) -
        gridboxRequestParams.recommendationItems.indexOf(b.erpNumber)
      );
    });

    return res;
  } catch (error) {
    throw new Error("Error fetching Gridbox data: " + error);
  }
};
